/**
 * Map Game Studio gallery assets → Phaser load URLs for Playtest Arena.
 *
 * Indexes exact basenames plus semantic aliases so Image Editor variants
 * (`player_paddle_edit_<ts>`, `…_nobg_<ts>_edit_<ts>`) still answer the
 * fixed texture keys the game loads (`player_paddle`).
 */

/**
 * @param {unknown} value
 * @returns {string}
 */
export function trim(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * @param {unknown} path
 * @returns {string}
 */
export function normalizeRelPath(path) {
  return trim(path).replace(/\\/g, '/').replace(/^\.\//, '');
}

/**
 * @param {unknown} path
 * @returns {string}
 */
export function basenameNoExt(path) {
  const norm = normalizeRelPath(path);
  const base = norm.split('/').filter(Boolean).pop() || '';
  return base.replace(/\.[a-z0-9]+$/i, '');
}

/**
 * Strip Image Editor / Remove Background variant suffixes.
 * e.g. player_paddle_nobg_123_edit_456 → player_paddle
 * @param {unknown} id
 * @returns {string}
 */
export function stripVariantSuffixes(id) {
  const raw = basenameNoExt(id);
  if (!raw) return '';
  return raw.replace(/_(nobg|edit)_\d+/gi, '');
}

/**
 * Firestore Timestamp / Date / number → epoch ms (0 if unknown).
 * @param {unknown} value
 * @returns {number}
 */
export function readTimestampMs(value) {
  if (value == null) return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'object') {
    if (typeof value.toMillis === 'function') {
      try {
        return value.toMillis();
      } catch {
        /* fall through */
      }
    }
    if (typeof value.seconds === 'number') {
      return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6);
    }
  }
  return 0;
}

/**
 * Best-effort recency for "newest variant wins".
 * Prefers updatedAt → createdAt → max *_edit_|*_nobg_ timestamp in id.
 * @param {object} asset
 * @returns {number}
 */
export function assetRecencyMs(asset) {
  if (!asset || typeof asset !== 'object') return 0;
  const fromFields = Math.max(
    readTimestampMs(asset.updatedAt),
    readTimestampMs(asset.createdAt),
  );
  if (fromFields > 0) return fromFields;

  const id = trim(asset.manifestAssetId) || trim(asset.id) || trim(asset.assetName);
  let maxTs = 0;
  const re = /_(?:nobg|edit)_(\d+)/gi;
  let m;
  while ((m = re.exec(id))) {
    const n = Number(m[1]);
    if (Number.isFinite(n) && n > maxTs) maxTs = n;
  }
  return maxTs;
}

/**
 * Status preference when recency ties (higher = better).
 * @param {object} asset
 * @returns {number}
 */
export function assetStatusRank(asset) {
  const status = trim(asset && asset.status).toLowerCase();
  if (status === 'approved' || status === 'success') return 3;
  if (status === 'needs_review') return 2;
  if (status === 'draft' || status === 'pending') return 1;
  return 0;
}

/**
 * @param {object} a
 * @param {object} b
 * @returns {number} positive if a should win over b
 */
export function comparePlaytestAssetPreference(a, b) {
  const recency = assetRecencyMs(a) - assetRecencyMs(b);
  if (recency !== 0) return recency;
  return assetStatusRank(a) - assetStatusRank(b);
}

/**
 * Resolve the semantic texture key for a gallery/manifest asset.
 * Follows sourceAssetId chain when present, then strips variant suffixes.
 * @param {object} asset
 * @param {Record<string, object>} [byId] manifestAssetId → asset
 * @returns {string}
 */
export function resolveSemanticAssetKey(asset, byId = {}) {
  if (!asset || typeof asset !== 'object') return '';

  let cur = asset;
  const seen = new Set();
  for (let hop = 0; hop < 16 && cur; hop += 1) {
    const id =
      trim(cur.manifestAssetId) ||
      trim(cur.id) ||
      trim(cur.assetName) ||
      '';
    if (!id || seen.has(id)) break;
    seen.add(id);

    const src = trim(cur.sourceAssetId);
    if (src && byId[src]) {
      cur = byId[src];
      continue;
    }
    // sourceAssetId may already be the root semantic id (no gallery row).
    if (src && !byId[src]) {
      return stripVariantSuffixes(src) || src;
    }
    return stripVariantSuffixes(id) || id;
  }

  const fallback =
    trim(asset.manifestAssetId) ||
    trim(asset.id) ||
    trim(asset.assetName) ||
    '';
  return stripVariantSuffixes(fallback) || fallback;
}

/**
 * Playtest texture source eligibility.
 *
 * Intentional denylist: only `rejected` / `failed` are excluded.
 *
 * @param {object} asset
 * @returns {boolean}
 */
export function isPlaytestGalleryAsset(asset) {
  if (!asset || typeof asset !== 'object') return false;
  if (!trim(asset.imageUrl) && !trim(asset.previewUrl)) return false;
  const status = trim(asset.status).toLowerCase();
  if (status === 'rejected' || status === 'failed') return false;
  return true;
}

/**
 * Index lookup keys for a semantic or exact basename.
 * @param {(key: string, url: string, asset: object) => void} put
 * @param {string} name
 * @param {string} url
 * @param {object} asset
 */
function putNameAliases(put, name, url, asset) {
  if (!name || !url) return;
  put(name, url, asset);
  put(`assets/${name}`, url, asset);
  put(`assets/${name}.png`, url, asset);
  put(`/assets/${name}.png`, url, asset);
  put(`public/assets/${name}.png`, url, asset);
}

/**
 * @param {unknown} galleryAssets
 * @returns {Record<string, string>} lookup key → Storage download URL
 */
export function buildPlaytestAssetUrlMap(galleryAssets) {
  /** @type {Record<string, string>} */
  const map = {};
  /** @type {Record<string, object>} winner asset per map key (for overwrite) */
  const winners = {};

  const rows = (Array.isArray(galleryAssets) ? galleryAssets : []).filter(
    isPlaytestGalleryAsset,
  );

  /** @type {Record<string, object>} */
  const byId = {};
  rows.forEach((asset) => {
    const id = trim(asset.manifestAssetId) || trim(asset.id);
    if (id) byId[id] = asset;
  });

  /**
   * Newest / best asset wins for a given lookup key (explicit tie-break).
   * @param {string} key
   * @param {string} url
   * @param {object} asset
   */
  const put = (key, url, asset) => {
    const k = normalizeRelPath(key);
    if (!k || !url || !asset) return;

    const apply = (mapKey) => {
      const prev = winners[mapKey];
      if (prev && comparePlaytestAssetPreference(asset, prev) <= 0) {
        return;
      }
      winners[mapKey] = asset;
      map[mapKey] = url;
    };

    apply(k);
    apply(k.toLowerCase());
  };

  rows.forEach((asset) => {
    const url = trim(asset.imageUrl) || trim(asset.previewUrl);
    if (!url) return;

    const exactNames = new Set();
    const githubPath = normalizeRelPath(asset.githubPath);
    if (githubPath) {
      exactNames.add(basenameNoExt(githubPath));
      put(githubPath, url, asset);
      put(githubPath.replace(/^public\//i, ''), url, asset);
    }

    [asset.manifestAssetId, asset.assetName, asset.name, asset.id]
      .map(trim)
      .filter(Boolean)
      .forEach((n) => exactNames.add(basenameNoExt(n)));

    exactNames.forEach((name) => putNameAliases(put, name, url, asset));

    const semantic = resolveSemanticAssetKey(asset, byId);
    if (semantic) {
      putNameAliases(put, semantic, url, asset);
    }

    // Also register stripped form of each exact name (legacy rows without
    // sourceAssetId still alias player_paddle_nobg_…_edit_… → player_paddle).
    exactNames.forEach((name) => {
      const stripped = stripVariantSuffixes(name);
      if (stripped && stripped !== name) {
        putNameAliases(put, stripped, url, asset);
      }
    });
  });

  return map;
}

/**
 * Resolve a Phaser load (key, url) against the gallery map.
 * @param {unknown} key
 * @param {unknown} url
 * @param {Record<string, string> | null | undefined} urlMap
 * @returns {string} remapped absolute URL, or original url
 */
export function remapPlaytestAssetUrl(key, url, urlMap) {
  const map = urlMap && typeof urlMap === 'object' ? urlMap : {};
  const candidates = [
    url,
    key,
    normalizeRelPath(url),
    normalizeRelPath(key),
    basenameNoExt(url),
    basenameNoExt(key),
    stripVariantSuffixes(url),
    stripVariantSuffixes(key),
  ]
    .map((c) => normalizeRelPath(String(c || '')))
    .filter(Boolean);

  for (const c of candidates) {
    if (map[c]) return map[c];
    if (map[c.toLowerCase()]) return map[c.toLowerCase()];
  }
  return typeof url === 'string' ? url : String(url || '');
}

/**
 * True when deleting `asset` would leave no other playtest-eligible
 * gallery row that still resolves to the same semantic texture key.
 * @param {object} asset
 * @param {unknown} galleryAssets
 * @returns {{ sole: boolean, semanticKey: string, peerCount: number }}
 */
export function evaluateSoleSemanticKeyHolder(asset, galleryAssets) {
  const rows = (Array.isArray(galleryAssets) ? galleryAssets : []).filter(
    isPlaytestGalleryAsset,
  );
  /** @type {Record<string, object>} */
  const byId = {};
  rows.forEach((row) => {
    const id = trim(row.manifestAssetId) || trim(row.id);
    if (id) byId[id] = row;
  });

  const semanticKey = resolveSemanticAssetKey(asset, byId);
  if (!semanticKey) {
    return { sole: false, semanticKey: '', peerCount: 0 };
  }

  const selfId =
    trim(asset.manifestAssetId) || trim(asset.id) || trim(asset.assetName);
  let peers = 0;
  rows.forEach((row) => {
    const rowId = trim(row.manifestAssetId) || trim(row.id);
    if (selfId && rowId === selfId) return;
    if (resolveSemanticAssetKey(row, byId) === semanticKey) {
      peers += 1;
    }
  });

  return { sole: peers === 0, semanticKey, peerCount: peers };
}
